import {
  FALLBACK_CENTRE,
  FIT_PADDING_PX,
  MAX_FIT_ZOOM,
  SHAPE_FILL_OPACITY,
  SHAPE_HOVER_OPACITY,
  SHAPE_OUTLINE,
  pinHolder,
  type LngLat,
  type MapSurface,
} from './surface.js'

export interface GoogleLibraries {
  core: google.maps.CoreLibrary
  maps: google.maps.MapsLibrary
  marker: google.maps.MarkerLibrary
}

/** Google's own id for trying Advanced Markers before a Map ID exists. */
const DEMO_MAP_ID = 'DEMO_MAP_ID'
const CALLBACK = '__bugBustersGoogleMaps'

let loading: Promise<GoogleLibraries> | null = null
let refused = false
const refusalListeners = new Set<() => void>()

/**
 * Loads the Maps JavaScript API once per page, however many maps ask.
 *
 * A script tag rather than a loader package: it is one URL and a callback, and
 * the libraries come from `importLibrary`, which is Google's supported way to
 * load them asynchronously.
 */
export function loadGoogleMaps(key: string): Promise<GoogleLibraries> {
  loading ??= new Promise<void>((resolve, reject) => {
    const globals = window as unknown as Record<string, unknown>
    globals[CALLBACK] = () => resolve()
    // Google calls this global by name when it rejects the key: wrong API,
    // wrong website, or billing off. The script itself loads fine, so this is
    // the only signal there is.
    globals['gm_authFailure'] = () => {
      refused = true
      for (const listener of refusalListeners) listener()
    }

    const script = document.createElement('script')
    const params = new URLSearchParams({ key, v: 'weekly', loading: 'async', callback: CALLBACK })
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`
    script.async = true
    script.onerror = () => {
      loading = null
      script.remove()
      reject(new Error('Google Maps could not be reached.'))
    }
    document.head.append(script)
  }).then(async () => {
    const [core, maps, marker] = await Promise.all([
      google.maps.importLibrary('core'),
      google.maps.importLibrary('maps'),
      google.maps.importLibrary('marker'),
    ])
    return {
      core: core as google.maps.CoreLibrary,
      maps: maps as google.maps.MapsLibrary,
      marker: marker as google.maps.MarkerLibrary,
    }
  })

  return loading
}

/** Called when Google rejects the key, including if it already has. Returns the unsubscribe. */
export function onGoogleRefusal(listener: () => void): () => void {
  if (refused) listener()
  refusalListeners.add(listener)
  return () => refusalListeners.delete(listener)
}

const toLatLng = ([lng, lat]: LngLat): google.maps.LatLngLiteral => ({ lat, lng })

export function createGoogleSurface(
  container: HTMLElement,
  { core, maps, marker }: GoogleLibraries,
  mapId: string | null,
): MapSurface {
  const map = new maps.Map(container, {
    center: toLatLng(FALLBACK_CENTRE),
    zoom: 10,
    // Advanced Markers draw only on a map with a Map ID.
    mapId: mapId ?? DEMO_MAP_ID,
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: 'greedy',
  })

  let markers: google.maps.marker.AdvancedMarkerElement[] = []
  let lines: google.maps.Polyline[] = []
  let shapeListeners: google.maps.MapsEventListener[] = []
  let fills: ReadonlyMap<string, string> = new Map()

  const shapeStyle = (feature: google.maps.Data.Feature): google.maps.Data.StyleOptions => {
    const fill = fills.get(String(feature.getId()))
    return {
      fillColor: fill ?? '#ffffff',
      fillOpacity: fill ? SHAPE_FILL_OPACITY : 0,
      strokeColor: SHAPE_OUTLINE,
      strokeOpacity: 0.7,
      strokeWeight: 0.75,
    }
  }

  return {
    setPins(pins) {
      for (const entry of markers) entry.map = null
      markers = pins.map((pin) => {
        // An Advanced Marker sits on its bottom edge; the pins are circles
        // and belong centred on the point, as MapLibre draws them.
        const holder = pinHolder(pin.element)
        holder.style.transform = 'translateY(50%)'
        return new marker.AdvancedMarkerElement({
          map,
          position: toLatLng(pin.lngLat),
          content: holder,
        })
      })
    },
    setLines(next) {
      for (const line of lines) line.setMap(null)
      lines = next
        .filter((line) => line.path.length > 1)
        .map(
          (line) =>
            new maps.Polyline({
              map,
              path: line.path.map(toLatLng),
              strokeColor: line.colour,
              strokeOpacity: line.opacity ?? 0.75,
              strokeWeight: line.width ?? 3,
            }),
        )
    },
    setShapes(shapes, handlers) {
      for (const listener of shapeListeners) listener.remove()
      shapeListeners = []
      map.data.forEach((feature) => map.data.remove(feature))
      if (!shapes) return

      map.data.addGeoJson(shapes, { idPropertyName: 'id' })
      map.data.setStyle(shapeStyle)
      shapeListeners = [
        map.data.addListener('click', (event: google.maps.Data.MouseEvent) =>
          handlers.onClick(String(event.feature.getId())),
        ),
        map.data.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
          map.data.overrideStyle(event.feature, {
            fillOpacity: fills.has(String(event.feature.getId())) ? SHAPE_HOVER_OPACITY : 0.15,
            fillColor: fills.get(String(event.feature.getId())) ?? SHAPE_OUTLINE,
            strokeWeight: 2,
          })
          handlers.onHover(String(event.feature.getId()))
        }),
        map.data.addListener('mouseout', (event: google.maps.Data.MouseEvent) => {
          map.data.revertStyle(event.feature)
          handlers.onHover(null)
        }),
      ]
    },
    fillShapes(next) {
      fills = next
      map.data.setStyle((feature) => shapeStyle(feature))
    },
    fit(points) {
      if (points.length === 0) return
      const bounds = new core.LatLngBounds()
      for (const point of points) bounds.extend(toLatLng(point))
      map.fitBounds(bounds, FIT_PADDING_PX)
      // `fitBounds` has no maximum zoom, and one pin would fill the screen.
      core.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM)
      })
    },
    destroy() {
      for (const entry of markers) entry.map = null
      for (const line of lines) line.setMap(null)
      for (const listener of shapeListeners) listener.remove()
      core.event.clearInstanceListeners(map)
    },
  }
}
